import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createVideoProvider } from '../providers';
import type { VideoProvider, VideoRoomState } from '../providers';
import type { VideoProviderConfig } from '../providers/types';
import { log } from '../lib/twilio-diag/logger';

export interface UseLiveVideoReturn extends VideoRoomState {
  connect: (token: string, roomName: string, config?: VideoProviderConfig) => Promise<void>;
  disconnect: () => void;
  /**
   * Resolves false when switching the device on failed to acquire it (the error
   * is already in `error`); never rejects, so a fire-and-forget call is safe.
   */
  toggleCamera: () => Promise<boolean>;
  /** Same contract as `toggleCamera`. */
  toggleMic: () => Promise<boolean>;
  localVideoRef: React.RefObject<HTMLVideoElement | null>;
  // Remote refs are callback refs so re-mount of the <video>/<audio> element
  // (e.g. consumer conditionally unmounts the video panel on mode-switch to chat
  // and back) triggers re-attach. A plain RefObject would not re-fire the attach
  // effect because state.remoteParticipants does not change on DOM remount.
  remoteVideoRef: (el: HTMLVideoElement | null) => void;
  remoteAudioRef: (el: HTMLAudioElement | null) => void;
  isCameraOn: boolean;
  isMicOn: boolean;
  /** Switch to a different camera or microphone mid-call */
  switchDevice: (kind: 'video' | 'audio', deviceId: string) => Promise<void>;
}

/**
 * Owns one video provider — building it on the first render and disconnecting it
 * on unmount.
 *
 * Call it exactly once, from LiveSessionProvider. Screens read the session it holds
 * through useVideoSession(); calling this hook again would open a second connection
 * to the same room, which is the double-join the customer used to make on the way
 * from the transition screen into the consultation.
 */
export function useLiveVideo(): UseLiveVideoReturn {
  // Built on the first render rather than in an effect. The session now sits above
  // the screens, and React runs a child's effects before its parent's — so by the
  // time this hook's effect ran, TransitionScreen had already called connect() on a
  // provider that did not exist yet, and the call silently did nothing. Nothing here
  // talks to Twilio until connect() is called, so building it early costs nothing.
  const [provider] = useState<VideoProvider>(createVideoProvider);

  const [state, setState] = useState<VideoRoomState>({
    isConnected: false,
    isConnecting: false,
    localParticipant: null,
    remoteParticipants: [],
    error: null,
    localNetworkQuality: 0,
    remoteNetworkQuality: 0,
  });

  // Local self-view sink. A plain RefObject would be attached only if the <video>
  // element happened to exist at the moment the track arrived. It no longer does:
  // the tracks are acquired on the transition screen and VideoPanel mounts a screen
  // later, on a provider that is now shared. Assigning through a setter turns that
  // mount into a state change, so the attach effect re-runs for the new element —
  // the same reason the remote sinks below are callback refs. The shape stays a
  // RefObject because VideoPanel reads `.current` to decide on mirroring.
  const localVideoElRef = useRef<HTMLVideoElement | null>(null);
  const [localVideoEl, setLocalVideoEl] = useState<HTMLVideoElement | null>(null);
  const localVideoRef = useMemo<React.RefObject<HTMLVideoElement | null>>(
    () => ({
      get current() {
        return localVideoElRef.current;
      },
      set current(el: HTMLVideoElement | null) {
        if (localVideoElRef.current === el) return;
        localVideoElRef.current = el;
        setLocalVideoEl(el);
      },
    }),
    []
  );
  const [remoteVideoEl, setRemoteVideoEl] = useState<HTMLVideoElement | null>(null);
  const [remoteAudioEl, setRemoteAudioEl] = useState<HTMLAudioElement | null>(null);
  const remoteVideoRef = useCallback((el: HTMLVideoElement | null) => {
    setRemoteVideoEl(el);
  }, []);
  const remoteAudioRef = useCallback((el: HTMLAudioElement | null) => {
    setRemoteAudioEl(el);
  }, []);

  // Subscribe to the provider built above, and own its teardown.
  useEffect(() => {
    const unsubscribe = provider.subscribe(setState);
    // Catch up on whatever the provider reported between construction and this
    // subscription: a consumer's mount effect runs first and can already have
    // called connect(), which raises `isConnecting` synchronously. Reading an
    // external system on subscribe is what this effect is for, cascading render
    // and all.
    setState(provider.getState());

    return () => {
      unsubscribe();
      provider.disconnect();
    };
  }, [provider]);

  // Attach local video when available
  useEffect(() => {
    if (state.localParticipant?.videoTrack && localVideoEl) {
      const vt = state.localParticipant.videoTrack;
      log('[twilio:local:track:sink]', {
        t: Date.now(),
        roomSid: null,
        participantSid: state.localParticipant.id ?? null,
        trackSid: vt.id ?? null,
        trackName: null,
        kind: vt.kind ?? 'video',
        enabled: vt.enabled ?? null,
        isStarted: null,
        source: 'local',
        op: 'attach',
        sinkTag: 'HTMLVideoElement',
      });
      vt.attach(localVideoEl);
    }
  }, [state.localParticipant?.videoTrack, localVideoEl]);

  // Attach remote video when available. Dep on remoteVideoEl ensures re-attach on
  // <video> element remount — e.g. consumer unmounts VideoPanel on mode-switch to
  // chat and remounts it on switch back; the new element needs the existing track.
  useEffect(() => {
    const remoteTrack = state.remoteParticipants[0]?.videoTrack;
    if (remoteTrack && remoteVideoEl) {
      log('[twilio:remote:track:sink]', {
        t: Date.now(),
        roomSid: null,
        participantSid: state.remoteParticipants[0]?.id ?? null,
        trackSid: remoteTrack.id ?? null,
        trackName: null,
        kind: remoteTrack.kind ?? 'video',
        enabled: remoteTrack.enabled ?? null,
        isStarted: null,
        source: 'remote',
        op: 'attach',
        sinkTag: 'HTMLVideoElement',
      });
      remoteTrack.attach(remoteVideoEl);
      // Autoplay policy can reject play() when user-activation is stale (buyer-first
      // scenario: customer has been waiting minutes before seller joins). The remote
      // <video> is muted, so play() should resolve; logging the outcome makes future
      // incidents diagnosable via window.__twilioDiag.dump().
      const playPromise = remoteVideoEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            log('[twilio:remote:video:play-ok]', {
              t: Date.now(),
              participantSid: state.remoteParticipants[0]?.id ?? null,
              trackSid: remoteTrack.id ?? null,
              muted: remoteVideoEl.muted,
            });
          })
          .catch((err: unknown) => {
            const error = err as { name?: string; message?: string } | null;
            log('[twilio:remote:video:play-rejected]', {
              t: Date.now(),
              participantSid: state.remoteParticipants[0]?.id ?? null,
              trackSid: remoteTrack.id ?? null,
              muted: remoteVideoEl.muted,
              name: error?.name ?? null,
              message: error?.message ?? null,
            });
          });
      }
    }
    return () => {
      if (remoteTrack && remoteVideoEl) {
        remoteTrack.detach();
      }
    };
  }, [state.remoteParticipants, remoteVideoEl]);

  // Attach remote audio to a separate hidden <audio> element. Keeping audio off the
  // remote <video> lets the video autoplay while muted (bypassing Chrome's
  // gesture-gated autoplay for media with audio).
  useEffect(() => {
    const remoteAudio = state.remoteParticipants[0]?.audioTrack;
    if (remoteAudio && remoteAudioEl) {
      log('[twilio:remote:track:sink]', {
        t: Date.now(),
        roomSid: null,
        participantSid: state.remoteParticipants[0]?.id ?? null,
        trackSid: remoteAudio.id ?? null,
        trackName: null,
        kind: remoteAudio.kind ?? 'audio',
        enabled: remoteAudio.enabled ?? null,
        isStarted: null,
        source: 'remote',
        op: 'attach',
        sinkTag: 'HTMLAudioElement',
      });
      remoteAudio.attach(remoteAudioEl);
      const playPromise = remoteAudioEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            log('[twilio:remote:audio:play-ok]', {
              t: Date.now(),
              participantSid: state.remoteParticipants[0]?.id ?? null,
              trackSid: remoteAudio.id ?? null,
            });
          })
          .catch((err: unknown) => {
            const error = err as { name?: string; message?: string } | null;
            log('[twilio:remote:audio:play-rejected]', {
              t: Date.now(),
              participantSid: state.remoteParticipants[0]?.id ?? null,
              trackSid: remoteAudio.id ?? null,
              name: error?.name ?? null,
              message: error?.message ?? null,
            });
          });
      }
    }
    return () => {
      if (remoteAudio && remoteAudioEl) {
        remoteAudio.detach();
      }
    };
  }, [state.remoteParticipants, remoteAudioEl]);

  const connect = useCallback(async (token: string, roomName: string, config?: VideoProviderConfig) => {
    await provider.connect(token, roomName, config);
  }, [provider]);

  const disconnect = useCallback(() => {
    provider.disconnect();
  }, [provider]);

  const toggleCamera = useCallback(async () => {
    try {
      await provider.toggleCamera();
      return true;
    } catch {
      return false;
    }
  }, [provider]);

  const toggleMic = useCallback(async () => {
    try {
      await provider.toggleMic();
      return true;
    } catch {
      return false;
    }
  }, [provider]);

  const switchDevice = useCallback(async (kind: 'video' | 'audio', deviceId: string) => {
    await provider.switchDevice(kind, deviceId);
  }, [provider]);

  // Derive camera/mic state from local participant.
  // Before connection: optimistically report "on" so initial UI does not flash a disabled state.
  // After connection: absence of a track means "off" — do not fall back to true, otherwise callers
  // that gate track-acquisition on isCameraOn/isMicOn skip work when tracks are legitimately missing
  // (e.g. connected in audio-only mode, later switching to video).
  const isCameraOn = state.localParticipant
    ? !!state.localParticipant.videoTrack?.enabled
    : true;
  const isMicOn = state.localParticipant
    ? !!state.localParticipant.audioTrack?.enabled
    : true;

  return {
    ...state,
    connect,
    disconnect,
    toggleCamera,
    toggleMic,
    switchDevice,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    isCameraOn,
    isMicOn,
  };
}
